/**
 * Base RTK Query — NexaCraft AI.
 *
 * Les endpoints appellent l'adaptateur src/lib/api/dataApi.ts, qui parle
 * uniquement au Worker via fetch('/api/v1/...'). Aucune donnée factice.
 *
 * Règle : tout état provenant de l'API vit ici (docs/01-ARCHITECTURE.md § 3).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { dataApi, type UsageEntry, type WidgetConfig } from './dataApi';
import type { ProTenant, CallLog, Opportunity, ServiceItem, AppNotification, LeadNote, DashboardStats } from '../../types';

export interface CallsListArgs {
  status?: string;
  limit?: number;
}

export const baseApi = createApi({
  reducerPath: 'api',
  tagTypes: ['Tenant', 'Professional', 'Call', 'Lead', 'Usage', 'Service', 'Subscription', 'Notification', 'Widget'],
  baseQuery: async () => {
    // Les endpoints ci-dessous utilisent queryFn ; ce baseQuery n'est jamais appelé.
    return { data: null };
  },
  endpoints: (builder) => ({
    // ── Tenant / professionnels ────────────────────────────────────────────
    getTenant: builder.query<ProTenant, string | undefined>({
      queryFn: async (tenantId) => {
        try {
          return { data: await dataApi.getTenant(tenantId) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: (_r, _e, tenantId) => [{ type: 'Tenant', id: tenantId ?? 'current' }],
    }),

    listProfessionals: builder.query<ProTenant[], void>({
      queryFn: async () => {
        try {
          return { data: await dataApi.listProfessionals() };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Professional'],
    }),

    updateTenant: builder.mutation<ProTenant, ProTenant>({
      queryFn: async (tenant) => {
        try {
          return { data: await dataApi.updateTenant(tenant) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: (_r, _e, tenant) => [{ type: 'Tenant', id: tenant.id }],
    }),

    // ── Sprint B : vérification + CRUD services ──────────────────────────
    submitVerification: builder.mutation<{ requestId: string; status: string }, { tenantId: string; documents?: string[] }>({
      queryFn: async ({ tenantId, documents }) => {
        try {
          return { data: await dataApi.submitVerification(tenantId, documents) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: (_r, _e, a) => [{ type: 'Tenant', id: a.tenantId }, 'Professional'],
    }),

    listServices: builder.query<ServiceItem[], void>({
      queryFn: async () => {
        try {
          return { data: await dataApi.listServices() };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Service'],
    }),

    createService: builder.mutation<ServiceItem, Omit<ServiceItem, 'id'>>({
      queryFn: async (service) => {
        try {
          return { data: await dataApi.createService(service) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Service', 'Tenant'],
    }),

    deleteService: builder.mutation<{ deleted: boolean }, string>({
      queryFn: async (serviceId) => {
        try {
          return { data: await dataApi.deleteService(serviceId) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Service', 'Tenant'],
    }),

    // ── Appels ─────────────────────────────────────────────────────────────
    listCalls: builder.query<CallLog[], CallsListArgs | void>({
      queryFn: async (args) => {
        try {
          return { data: await dataApi.listCalls(args ?? undefined) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Call'],
    }),

    /** Journal client (GET /my-calls) — skip si pas de rôle client. */
    listMyCalls: builder.query<CallLog[], number | void>({
      queryFn: async (limit) => {
        try {
          return { data: await dataApi.listMyCalls(typeof limit === 'number' ? limit : undefined) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Call'],
    }),

    // ── Opportunités ───────────────────────────────────────────────────────
    listLeads: builder.query<Opportunity[], string | void>({
      queryFn: async (tenantId) => {
        try {
          return { data: await dataApi.listLeads(tenantId ?? undefined) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Lead'],
    }),

    updateLeadStage: builder.mutation<Opportunity, { leadId: string; stage: Opportunity['stage'] }>({
      queryFn: async ({ leadId, stage }) => {
        try {
          return { data: await dataApi.updateLeadStage(leadId, stage) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      // Mise à jour optimiste autorisée : changement de statut d'opportunité
      // fait partie des cas recommandés (docs/01-ARCHITECTURE.md § 4).
      onQueryStarted: async ({ leadId, stage }, { dispatch, queryFulfilled, getState }) => {
        const state = getState() as { api?: { queries?: Record<string, unknown> } };
        void state;
        const patch = dispatch(
          baseApi.util.updateQueryData('listLeads', undefined, (draft: Opportunity[]) => {
            const lead = draft.find((l) => l.id === leadId);
            if (lead) lead.stage = stage;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['Lead'],
    }),

    // ── Détail opportunité + notes CRM ───────────────────────────────────
    getLeadDetail: builder.query<Opportunity & { notes: LeadNote[] }, string>({
      queryFn: async (leadId) => {
        try {
          return { data: await dataApi.getLeadDetail(leadId) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: (_r, _e, leadId) => [{ type: 'Lead', id: leadId }],
    }),

    addLeadNote: builder.mutation<LeadNote, { leadId: string; note: string }>({
      queryFn: async ({ leadId, note }) => {
        try {
          return { data: await dataApi.addLeadNote(leadId, note) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: (_r, _e, { leadId }) => [
        { type: 'Lead', id: leadId },
        'Lead',
      ],
    }),

    // ── Notifications in-app ─────────────────────────────────────────────
    listNotifications: builder.query<AppNotification[], void>({
      queryFn: async () => {
        try {
          return { data: await dataApi.listNotifications() };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Notification'],
    }),

    markNotificationRead: builder.mutation<{ read: boolean }, string>({
      queryFn: async (notificationId) => {
        try {
          return { data: await dataApi.markNotificationRead(notificationId) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      // Optimiste : le badge se décrémente instantanément.
      onQueryStarted: async (notificationId, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          baseApi.util.updateQueryData('listNotifications', undefined, (draft: AppNotification[]) => {
            const n = draft.find((item) => item.id === notificationId);
            if (n) n.read = true;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // ── Tableau de bord temps réel (GET /dashboard/stats) ───────────
    getUsageHistory: builder.query<UsageEntry[], number | undefined>({
      queryFn: async (limit) => {
        try {
          return { data: await dataApi.getUsageHistory(limit) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Usage'],
    }),

    getDashboardStats: builder.query<DashboardStats, string | undefined>({
      queryFn: async (month) => {
        try {
          return { data: await dataApi.getDashboardStats(month) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      // Rafraîchit les agrégats dès qu'un appel ou une opportunité bouge.
      providesTags: ['Call', 'Lead'],
    }),

    // ── Widget d'intégration (bouton d'appel sur le site du pro) ─────────
    getWidgetConfig: builder.query<WidgetConfig, void>({
      queryFn: async () => {
        try {
          return { data: await dataApi.getWidgetConfig() };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Widget'],
    }),

    updateWidgetConfig: builder.mutation<WidgetConfig, WidgetConfig>({
      queryFn: async (config) => {
        try {
          return { data: await dataApi.updateWidgetConfig(config) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Widget'],
    }),

  }),
});

export const {
  useGetTenantQuery,
  useListProfessionalsQuery,
  useUpdateTenantMutation,
  useSubmitVerificationMutation,
  useListServicesQuery,
  useCreateServiceMutation,
  useDeleteServiceMutation,
  useListCallsQuery,
  useListMyCallsQuery,
  useListLeadsQuery,
  useUpdateLeadStageMutation,
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useGetLeadDetailQuery,
  useLazyGetLeadDetailQuery,
  useAddLeadNoteMutation,
  useGetDashboardStatsQuery,
  useGetUsageHistoryQuery,
  useGetWidgetConfigQuery,
  useUpdateWidgetConfigMutation,
} = baseApi;
